import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Receipt, BookOpen, LayoutDashboard, Settings } from 'lucide-react';

/**
 * AdminBottomNav
 * --------------
 * Sticky 4-tab bottom navigation used on phones / handheld POS devices.
 * Hidden on `lg` and above (desktop / large tablet landscape).
 *
 * `newOrderCount` (optional) — renders a red badge on the Orders tab so
 *  staff can see new paid-unprinted orders even when looking at other pages.
 */
const TABS = [
  { to: '/admin/orders',   icon: Receipt,         label: 'Orders'    },
  { to: '/admin/menu',     icon: BookOpen,        label: 'Menu'      },
  { to: '/admin/dashboard',icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/settings', icon: Settings,        label: 'Settings'  },
];

const isActive = (current, target) => {
  if (target === '/admin/orders') return current.startsWith('/admin/orders');
  if (target === '/admin/menu')   return current.startsWith('/admin/menu') || current.startsWith('/admin/categor');
  return current.startsWith(target);
};

export const AdminBottomNav = ({ newOrderCount = 0 }) => {
  const { pathname } = useLocation();

  return (
    <nav
      data-testid="admin-bottom-nav"
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-stone-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
      aria-label="Admin navigation"
    >
      {TABS.map(({ to, icon: Icon, label }) => {
        const active = isActive(pathname, to);
        const showBadge = to === '/admin/orders' && newOrderCount > 0;
        return (
          <Link
            key={to}
            to={to}
            data-testid={`bottom-nav-${label.toLowerCase()}`}
            className={`relative flex h-16 flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
              active ? 'text-red-600' : 'text-stone-500 hover:text-stone-900'
            }`}
          >
            <span className="relative">
              <Icon className="h-6 w-6" />
              {showBadge && (
                <span className="absolute -right-2 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 py-0 text-[10px] font-bold leading-[16px] text-white shadow">
                  {newOrderCount > 9 ? '9+' : newOrderCount}
                </span>
              )}
            </span>
            <span>{label}</span>
            {active && (
              <span aria-hidden className="absolute inset-x-6 top-0 h-0.5 rounded-b bg-red-600" />
            )}
          </Link>
        );
      })}
    </nav>
  );
};
